import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MoreHorizontal } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSidecarMode } from '../../context/SidecarModeContext';
import { useColourPalette } from '../../context/ColourPaletteContext';
import { VISION_TYPES, VISION_TYPE_LABELS, getColourPalette } from '../../utils/colourPalettes';
import { useJobStore } from '../../stores/jobStore';
import JobBell from './JobBell';
import { HelpTrigger } from '../HelpPanel';
import { useFeatureStore } from '../../stores/featureStore';
import '../../styles/LiquidGlassHeader.css';

const LiquidGlassHeader: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const { sidecarMode, isAnimating, toggleSidecarMode } = useSidecarMode();
  const { visionType, setVisionType } = useColourPalette();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [a11yOpen, setA11yOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  // Below this width the four secondary action icons collapse into a single "More" trigger.
  const [isNarrow, setIsNarrow] = useState(false);
  const [isDark, setIsDark] = useState(() => {
    try {
      return localStorage.getItem('theme') === 'dark';
    } catch {
      return false;
    }
  });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const a11yRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  // Active background-job count, mirrored from JobBell, to surface an indicator on the
  // collapsed "More" trigger when the bell itself is hidden behind it.
  const activeJobCount = useJobStore((s) =>
    s.jobs.filter((j) => j.status === 'queued' || j.status === 'running').length
  );

  // Apply theme class to body AND html on mount and change
  // body.theme-dark drives CSS variables, html.theme-dark drives Tailwind dark: variants
  useEffect(() => {
    document.body.classList.toggle('theme-dark', isDark);
    document.body.classList.toggle('theme-light', !isDark);
    document.documentElement.classList.toggle('theme-dark', isDark);
    document.documentElement.classList.toggle('dark', isDark);
    try {
      localStorage.setItem('theme', isDark ? 'dark' : 'light');
    } catch {
      // Ignore persistence failures; the current theme can still apply.
    }
  }, [isDark]);

  const handleToggle = () => {
    toggleSidecarMode(toggleRef.current);
  };

  // Collapse the four secondary action icons into a single trigger below 1100px.
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1100px)');
    const update = () => setIsNarrow(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const { isModuleEnabled } = useFeatureStore();

  const isGenomicsActive = location.pathname.startsWith('/genomics');
  const isChemistryActive = location.pathname.startsWith('/chemistry');
  const isOmicsActive = location.pathname.startsWith('/omics');
  const isAssaysActive = location.pathname.startsWith('/biology');
  const isWorkbenchActive = location.pathname.startsWith('/hubs') || location.pathname.startsWith('/workbench');
  // const isSpectraActive = location.pathname.startsWith('/spectra');
  const isPipelineActive = location.pathname.startsWith('/pipeline');
  // isHubsActive removed — /hubs routes redirected to /workbench in v1.2.159
  const isElnActive = location.pathname === '/workbench/eln';

  const chemistryLink = '/chemistry';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
      if (a11yRef.current && !a11yRef.current.contains(event.target as Node)) {
        setA11yOpen(false);
      }
      if (moreRef.current && !moreRef.current.contains(event.target as Node)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getDisplayName = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    if (user?.first_name) {
      return user.first_name;
    }
    if (user?.username) {
      return user.username;
    }
    if (user?.email) {
      return user.email.split('@')[0];
    }
    return 'User';
  };

  const getInitial = () => {
    if (user?.first_name) return user.first_name.charAt(0).toUpperCase();
    if (user?.username) return user.username.charAt(0).toUpperCase();
    if (user?.email) return user.email.charAt(0).toUpperCase();
    return 'U';
  };

  const handleLogout = () => {
    setDropdownOpen(false);
    logout();
    navigate('/login');
  };

  // Theme + colour-vision controls are defined once and rendered either inline (wide)
  // or inside the collapsed "More" menu (narrow) — same handlers, same behaviour.
  const themeControl = (
    <button
      className="liquid-glass-header__theme-toggle"
      onClick={() => setIsDark(d => !d)}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {isDark ? (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="5" />
          <line x1="12" y1="1" x2="12" y2="3" />
          <line x1="12" y1="21" x2="12" y2="23" />
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
          <line x1="1" y1="12" x2="3" y2="12" />
          <line x1="21" y1="12" x2="23" y2="12" />
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
        </svg>
      ) : (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      )}
    </button>
  );

  const a11yControl = (
    <div className="liquid-glass-header__a11y-toggle" ref={a11yRef}>
      <button
        className={`liquid-glass-header__theme-toggle${visionType !== 'normal' ? ' liquid-glass-header__theme-toggle--active' : ''}`}
        onClick={() => setA11yOpen(o => !o)}
        title="Colour vision settings"
        aria-label="Colour vision settings"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      </button>
      {a11yOpen && (
        <div className="liquid-glass-header__a11y-popover">
          <div className="liquid-glass-header__a11y-title">Colour Vision</div>
          {VISION_TYPES.map((type) => (
            <button
              key={type}
              className={`liquid-glass-header__a11y-option ${visionType === type ? 'liquid-glass-header__a11y-option--active' : ''}`}
              onClick={() => { setVisionType(type); setA11yOpen(false); }}
            >
              <span className="liquid-glass-header__a11y-label">{VISION_TYPE_LABELS[type]}</span>
              <div className="liquid-glass-header__a11y-preview">
                {[1, 2, 3, 4, 5].map((zone) => (
                  <span
                    key={zone}
                    className="liquid-glass-header__a11y-circle"
                    style={{ backgroundColor: getColourPalette(type).zones[zone] }}
                  />
                ))}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );


  return (
    <header className={`liquid-glass-header ${sidecarMode ? 'liquid-glass-header--sidecar' : ''}`}>
      {/* Scanning line when active */}
      {sidecarMode && !isAnimating && (
        <div className="liquid-glass-header__scanning-line" />
      )}

      <div className="liquid-glass-header__content">
        <div className="liquid-glass-header__brand">
          <Link
            to="/chemistry/projects"
            className="liquid-glass-header__brand-link"
            style={{ textDecoration: 'none' }}
          >
            {/* Chaos-to-order icon: 5 vertical bars, fragmented → solid */}
            <svg className="liquid-glass-header__logo-icon" viewBox="0 0 40 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Bar 1 — most fragmented */}
              <rect x="2" y="2" width="4" height="5" rx="1" fill="currentColor" opacity="0.25" />
              <rect x="2" y="10" width="4" height="4" rx="1" fill="currentColor" opacity="0.3" />
              <rect x="2" y="18" width="4" height="3" rx="1" fill="currentColor" opacity="0.2" />
              <rect x="2" y="25" width="4" height="5" rx="1" fill="currentColor" opacity="0.35" />
              {/* Bar 2 */}
              <rect x="10" y="3" width="4" height="7" rx="1" fill="currentColor" opacity="0.4" />
              <rect x="10" y="14" width="4" height="5" rx="1" fill="currentColor" opacity="0.45" />
              <rect x="10" y="23" width="4" height="7" rx="1" fill="currentColor" opacity="0.5" />
              {/* Bar 3 — middle */}
              <rect x="18" y="2" width="4" height="10" rx="1" fill="currentColor" opacity="0.6" />
              <rect x="18" y="16" width="4" height="14" rx="1" fill="currentColor" opacity="0.65" />
              {/* Bar 4 */}
              <rect x="26" y="1" width="4" height="14" rx="1" fill="currentColor" opacity="0.8" />
              <rect x="26" y="18" width="4" height="13" rx="1" fill="currentColor" opacity="0.85" />
              {/* Bar 5 — fully solid */}
              <rect x="34" y="1" width="4" height="30" rx="1" fill="currentColor" opacity="1" />
            </svg>
            {/* Wordmark: ADROIT AI small/muted above, Adroit primary below */}
            <div className="liquid-glass-header__wordmark">
              <span className="liquid-glass-header__wordmark-super">ADROIT AI</span>
              <span className="liquid-glass-header__wordmark-primary">Adroit</span>
            </div>
          </Link>
        </div>

        <nav className="liquid-glass-header__nav">
            {/* Full mode tabs */}
            <div className={`liquid-glass-header__nav-group ${sidecarMode ? 'hidden' : 'visible'}`}>
              <Link
                to={chemistryLink}
                className={`liquid-glass-header__nav-link ${
                  isChemistryActive ? 'liquid-glass-header__nav-link--active' : ''
                }`}
              >
                Chemistry
              </Link>
              {isModuleEnabled('mod_biologics') && (
              <Link
                to="/omics"
                className={`liquid-glass-header__nav-link ${
                  isOmicsActive ? 'liquid-glass-header__nav-link--active' : ''
                }`}
              >
                Biologics
              </Link>
              )}
              {isModuleEnabled('mod_assays') && (
              <Link
                to="/biology/assays"
                className={`liquid-glass-header__nav-link ${
                  isAssaysActive ? 'liquid-glass-header__nav-link--active' : ''
                }`}
              >
                Assays
              </Link>
              )}
              {/* Hidden per Don's feedback — routes still active */}
              {/* Spectra nav item removed — route kept for direct access */}
              {isModuleEnabled('mod_genomics') && (
              <Link
                to="/genomics"
                className={`liquid-glass-header__nav-link ${
                  isGenomicsActive ? 'liquid-glass-header__nav-link--active' : ''
                }`}
              >
                Genomics
              </Link>
              )}
              {isModuleEnabled('mod_pipeline') && (
              <Link
                to="/pipeline"
                className={`liquid-glass-header__nav-link ${
                  isPipelineActive ? 'liquid-glass-header__nav-link--active' : ''
                }`}
              >
                Pipeline
              </Link>
              )}
              <Link
                to="/hubs"
                className={`liquid-glass-header__nav-link ${
                  isWorkbenchActive && !isElnActive ? 'liquid-glass-header__nav-link--active' : ''
                }`}
              >
                Toolbox
              </Link>
              {isModuleEnabled('mod_eln') && (
              <a
                href="/workbench-api/eln/"
                className={`liquid-glass-header__nav-link ${
                  isElnActive ? 'liquid-glass-header__nav-link--active' : ''
                }`}
                onClick={(e) => {
                  // Bypass the SPA router: a plain href is intercepted by the
                  // catch-all route and bounces to /chemistry.
                  e.preventDefault();
                  e.stopPropagation();
                  window.location.assign('/workbench-api/eln/');
                }}
              >
                ELN
              </a>
              )}
            </div>

            {/* Sidecar mode label */}
            <div className={`liquid-glass-header__nav-group ${sidecarMode ? 'visible' : 'hidden'}`}>
              <span className="liquid-glass-header__nav-link liquid-glass-header__nav-link--external liquid-glass-header__nav-link--active">
                Discovery Mode
              </span>
            </div>
        </nav>

        <div className="liquid-glass-header__actions">
          {!isNarrow ? (
            <>
              {/* Job Queue Bell */}
              {isAuthenticated && <JobBell />}

              {/* Help Panel */}
              <HelpTrigger />

              {/* Theme Toggle */}
              {themeControl}

              {/* Accessibility / Colour Vision Toggle */}
              {a11yControl}
            </>
          ) : (
            /* Narrow: collapse the four secondary icons into a single trigger + dropdown */
            <div className="liquid-glass-header__more" ref={moreRef}>
              <button
                className="liquid-glass-header__theme-toggle liquid-glass-header__more-trigger"
                onClick={() => setMoreOpen(o => !o)}
                title="More options"
                aria-label="More options"
                aria-haspopup="true"
                aria-expanded={moreOpen}
              >
                <MoreHorizontal size={18} />
                {isAuthenticated && activeJobCount > 0 && (
                  <span className="liquid-glass-header__more-dot" aria-hidden="true" />
                )}
              </button>

              {moreOpen && (
                <div className="liquid-glass-header__more-menu">
                  {isAuthenticated && (
                    <div className="liquid-glass-header__more-row">
                      <span className="liquid-glass-header__more-row-label">Background jobs</span>
                      <JobBell />
                    </div>
                  )}
                  <div className="liquid-glass-header__more-row">
                    <span className="liquid-glass-header__more-row-label">Help</span>
                    <HelpTrigger />
                  </div>
                  <div className="liquid-glass-header__more-row">
                    <span className="liquid-glass-header__more-row-label">
                      {isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                    </span>
                    {themeControl}
                  </div>
                  <div className="liquid-glass-header__more-row">
                    <span className="liquid-glass-header__more-row-label">Colour vision</span>
                    {a11yControl}
                  </div>
                </div>
              )}
            </div>
          )}

          {isAuthenticated && user ? (
            <div className="liquid-glass-header__user-dropdown" ref={dropdownRef}>
              <button
                className="liquid-glass-header__user-trigger"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                aria-expanded={dropdownOpen}
                aria-haspopup="true"
              >
                <div className="liquid-glass-header__avatar">{getInitial()}</div>
                <span className="liquid-glass-header__user-name">{getDisplayName()}</span>
                <svg
                  className={`liquid-glass-header__dropdown-arrow ${dropdownOpen ? 'open' : ''}`}
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                >
                  <path
                    d="M3 4.5L6 7.5L9 4.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {dropdownOpen && (
                <div className="liquid-glass-header__dropdown-menu">
                  <div className="liquid-glass-header__dropdown-info">
                    <span className="liquid-glass-header__dropdown-name">{getDisplayName()}</span>
                    {user?.email && (
                      <span className="liquid-glass-header__dropdown-email">{user.email}</span>
                    )}
                  </div>
                  <div className="liquid-glass-header__dropdown-divider" />
                  <Link
                    to="/profile"
                    className="liquid-glass-header__dropdown-item"
                    onClick={() => setDropdownOpen(false)}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M8 2C9.10457 2 10 2.89543 10 4C10 5.10457 9.10457 6 8 6C6.89543 6 6 5.10457 6 4C6 2.89543 6.89543 2 8 2Z" stroke="currentColor" strokeWidth="1.5" />
                      <path d="M4 13C4 10.7909 5.79086 9 8 9C10.2091 9 12 10.7909 12 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                    Profile
                  </Link>
                  {user?.role === 'admin' && (
                    <Link
                      to="/admin"
                      className="liquid-glass-header__dropdown-item"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M13.3333 14V12.6667C13.3333 11.9594 13.0524 11.2811 12.5523 10.781C12.0522 10.281 11.3739 10 10.6667 10H5.33333C4.62609 10 3.94781 10.281 3.44772 10.781C2.94762 11.2811 2.66667 11.9594 2.66667 12.6667V14"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8 7.33333C9.47276 7.33333 10.6667 6.13943 10.6667 4.66667C10.6667 3.19391 9.47276 2 8 2C6.52724 2 5.33333 3.19391 5.33333 4.66667C5.33333 6.13943 6.52724 7.33333 8 7.33333Z"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M14 5L14.5 5.5L13 7"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Admin
                    </Link>
                  )}
                  <button className="liquid-glass-header__dropdown-item" onClick={handleLogout}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M6 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V3.33333C2 2.97971 2.14048 2.64057 2.39052 2.39052C2.64057 2.14048 2.97971 2 3.33333 2H6"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M10.6667 11.3333L14 8L10.6667 4.66667"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M14 8H6"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="liquid-glass-header__user">
              <div className="liquid-glass-header__avatar">U</div>
              <span>User</span>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default LiquidGlassHeader;
